import { useRef, useState, useCallback } from 'react';
import { useFullScreen } from 'react-browser-hooks';

// feature detection for request fullscreen api
function documentHasFullscreenSupport() {
  const docBody = document.body as any;
  return !!(
    docBody.requestFullscreen ||
    docBody.webkitRequestFullscreen ||
    docBody.mozRequestFullscreen ||
    docBody.msRequestFullscreen
  );
}

// handles all the logic to have a fullscreen api for a map
export const useMapFullscreen = (map: L.Map | undefined) => {
  const element = useRef<HTMLDivElement>(null);
  const fs = useFullScreen({ element });
  const [fakeFullscreen, setFakeFullscreen] = useState(false);
  const emulateFullscreen = !documentHasFullscreenSupport();
  const isFullscreen = fs.fullScreen || fakeFullscreen;

  const invalidateLeaflet = useCallback(() => map?.invalidateSize(), [map]);

  const maximizeMap = useCallback(() => {
    if (emulateFullscreen) {
      // browser does not support fullscreen api, use emulation as fallback
      setFakeFullscreen(true);
      setTimeout(invalidateLeaflet, 1000);
    } else {
      const fsPromise = fs.open();
      if (!fsPromise) {
        // browser fullscreen api does not return a promise, wait 1 second
        setTimeout(invalidateLeaflet, 1000);
      } else {
        fsPromise.catch(() => {}).then(invalidateLeaflet);
      }
    }
  }, [fs.open, emulateFullscreen, invalidateLeaflet]);

  const minimizeMap = useCallback(() => {
    if (emulateFullscreen) {
      // browser does not support fullscreen api, use emulation as fallback
      setFakeFullscreen(false);
      setTimeout(invalidateLeaflet, 1000);
    } else {
      const fsPromise = fs.close();
      if (!fsPromise) {
        // browser fullscreen api does not return a promise, wait 1 second
        setTimeout(invalidateLeaflet, 1000);
      } else {
        fsPromise.catch(() => {}).then(invalidateLeaflet);
      }
    }
  }, [fs.close, emulateFullscreen, invalidateLeaflet]);

  return { element, isFullscreen, maximizeMap, minimizeMap };
};
